import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, TrashIcon, PencilSquareIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';
import { hoyLocal } from '../utils/fechaLocal';

interface Account {
  id: string;
  name: string;
  kind: string;
  bankName?: string | null;
  openingBalance: number;
  active: number;
  isDefault: number;
  ingresos?: number;
  egresos?: number;
  balance?: number;
}

const KINDS = [
  { value: 'efectivo', label: 'Efectivo / Caja' },
  { value: 'banco', label: 'Banco' },
  { value: 'billetera', label: 'Billetera / MP' },
  { value: 'otro', label: 'Otro' },
];
const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.map(k => [k.value, k.label]));
const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);

const emptyForm = { id: '', name: '', kind: 'banco', bankName: '', openingBalance: '', isDefault: false };

const FinancialAccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Fecha de corte: los saldos cuentan movimientos desde este día (lo anterior no suma plata)
  const [startDate, setStartDate] = useState<string>('');
  const [editingStart, setEditingStart] = useState(false);
  const [startDraft, setStartDraft] = useState('');

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/financial-accounts', { params: { all: 1, balances: 1 } });
      setAccounts(res.data.accounts || []);
      setStartDate(res.data.startDate || '');
    } catch { toast.error('Error al cargar cuentas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAccounts(); }, []);

  const handleSaveStartDate = async () => {
    if (!startDraft) { toast.error('Elegí una fecha'); return; }
    try {
      await axios.put('/api/financial-accounts/start-date', { date: startDraft });
      toast.success('Fecha de inicio actualizada');
      setEditingStart(false);
      fetchAccounts();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const totalDisponible = useMemo(() => accounts.filter(a => a.active).reduce((s, a) => s + (a.balance || 0), 0), [accounts]);

  const openNew = () => { setForm(emptyForm); setShowDialog(true); };
  const openEdit = (a: Account) => {
    setForm({ id: a.id, name: a.name, kind: a.kind, bankName: a.bankName || '', openingBalance: String(a.openingBalance ?? ''), isDefault: !!a.isDefault });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Poné un nombre'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), kind: form.kind, bankName: form.bankName || null, openingBalance: Number(form.openingBalance) || 0, isDefault: form.isDefault };
      if (form.id) await axios.put(`/api/financial-accounts/${form.id}`, payload);
      else await axios.post('/api/financial-accounts', payload);
      toast.success('Cuenta guardada');
      setShowDialog(false);
      fetchAccounts();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (a: Account) => {
    if (!(await confirmDialog({ title: 'Dar de baja la cuenta', description: `¿Dar de baja "${a.name}"? Los movimientos históricos se conservan.`, confirmText: 'Dar de baja' }))) return;
    try {
      await axios.delete(`/api/financial-accounts/${a.id}`);
      toast.success('Cuenta dada de baja');
      fetchAccounts();
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Cuentas</h2>
          <p className="text-sm text-muted-foreground">Efectivo, bancos y billeteras propias. Se usan para indicar de/hacia qué cuenta entra o sale la plata.</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            {editingStart ? (
              <>
                <span>Los saldos cuentan desde</span>
                <DatePicker value={startDraft} onChange={setStartDraft} className="h-7 w-[140px] text-xs" />
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleSaveStartDate}>Guardar</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingStart(false)}>Cancelar</Button>
              </>
            ) : (
              <>
                <span>
                  {startDate
                    ? <>Los saldos cuentan movimientos desde el <strong>{startDate.split('-').reverse().join('/')}</strong>. Lo anterior queda en la historia pero no suma plata.</>
                    : 'Sin fecha de corte: los saldos suman TODO el histórico.'}
                </span>
                <button type="button" className="underline hover:text-foreground" onClick={() => { setStartDraft(startDate || hoyLocal()); setEditingStart(true); }}>
                  cambiar
                </button>
              </>
            )}
          </div>
        </div>
        <Button onClick={openNew} size="sm" className="h-9 rounded-lg gap-1.5 bg-black dark:bg-white text-white dark:text-black">
          <PlusIcon className="h-4 w-4" /> Nueva cuenta
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">Disponible total</p>
          <p className="text-xl font-bold mt-0.5">{fmtARS(totalDisponible)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">Cuentas activas</p>
          <p className="text-xl font-bold mt-0.5">{accounts.filter(a => a.active).length}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <BanknotesIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No hay cuentas cargadas</p>
              <Button onClick={openNew} variant="outline" size="sm" className="mt-3">Crear la primera</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="h-9 px-3 text-xs">Cuenta</TableHead>
                  <TableHead className="h-9 px-3 text-xs">Tipo</TableHead>
                  <TableHead className="h-9 px-3 text-xs text-right">Saldo inicial</TableHead>
                  <TableHead className="h-9 px-3 text-xs text-right">Ingresos</TableHead>
                  <TableHead className="h-9 px-3 text-xs text-right">Egresos</TableHead>
                  <TableHead className="h-9 px-3 text-xs text-right">Saldo actual</TableHead>
                  <TableHead className="h-9 w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map(a => (
                  <TableRow key={a.id} className={`border-b hover:bg-muted/30 ${!a.active ? 'opacity-50' : ''}`}>
                    <TableCell className="px-3 py-2 text-sm font-medium">
                      {a.name}
                      {a.bankName ? <span className="text-muted-foreground"> · {a.bankName}</span> : ''}
                      {a.isDefault ? <Badge variant="secondary" className="ml-2 text-[10px]">Por defecto</Badge> : null}
                      {!a.active ? <Badge variant="outline" className="ml-2 text-[10px]">Baja</Badge> : null}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-sm"><Badge variant="outline" className="text-xs">{KIND_LABEL[a.kind] || a.kind}</Badge></TableCell>
                    <TableCell className="px-3 py-2 text-sm text-right text-muted-foreground">{fmtARS(a.openingBalance)}</TableCell>
                    <TableCell className="px-3 py-2 text-sm text-right text-emerald-600">{fmtARS(a.ingresos || 0)}</TableCell>
                    <TableCell className="px-3 py-2 text-sm text-right text-red-500">{fmtARS(a.egresos || 0)}</TableCell>
                    <TableCell className="px-3 py-2 text-sm text-right font-semibold">{fmtARS(a.balance || 0)}</TableCell>
                    <TableCell className="px-2 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Tip label="Editar"><Button variant="ghost" size="icon" className="size-7" onClick={() => openEdit(a)}><PencilSquareIcon className="h-3.5 w-3.5" /></Button></Tip>
                        {a.active ? <Tip label="Dar de baja"><Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => handleDelete(a)}><TrashIcon className="h-3.5 w-3.5" /></Button></Tip> : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={o => { if (!o) setShowDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{form.id ? 'Editar cuenta' : 'Nueva cuenta'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Banco Galicia" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={v => setForm(f => ({ ...f, kind: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Saldo inicial</Label>
                <Input type="number" value={form.openingBalance} onChange={e => setForm(f => ({ ...f, openingBalance: e.target.value }))} placeholder="0" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Banco / detalle (opcional)</Label>
              <Input value={form.bankName} onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))} placeholder="Ej: CBU / alias / titular" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
              Usar como cuenta por defecto
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialAccountsPage;
